import React, { useEffect, useMemo, useState } from 'react'
import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material'
import { useDebounce } from 'use-debounce'
import api from 'utils/api'

const MIN_QUERY_LENGTH = 2

export default function AdminFilterAutocomplete({ type, label, value, onChange, placeholder }) {
  const [input, setInput] = useState('')
  const [debouncedInput] = useDebounce(input, 350)
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const query = debouncedInput.trim()
    if (query.length < MIN_QUERY_LENGTH) {
      setOptions([])
      setLoading(false)
      return undefined
    }
    let current = true
    setLoading(true)
    api.get(`/admin/filter-options?type=${encodeURIComponent(type)}&q=${encodeURIComponent(query)}`)
      .then(data => { if (current) setOptions(data || []) })
      .catch(() => { if (current) setOptions([]) })
      .finally(() => { if (current) setLoading(false) })
    return () => { current = false }
  }, [debouncedInput, type])

  const selected = useMemo(() => {
    if (!value) return null
    return options.find(option => option.value === value) || { value, label: value }
  }, [options, value])

  return <Autocomplete
    value={selected}
    options={options}
    loading={loading}
    filterOptions={available => available}
    isOptionEqualToValue={(option, selectedOption) => option.value === selectedOption.value}
    getOptionLabel={option => option?.label || option?.value || ''}
    onInputChange={(_, nextInput, reason) => { if (reason === 'input' || reason === 'clear') setInput(nextInput) }}
    onChange={(_, option) => onChange(option?.value || '')}
    noOptionsText={input.trim().length < MIN_QUERY_LENGTH ? 'Type at least 2 characters' : 'No matching options'}
    renderOption={(props, option) => <Box component="li" {...props} key={`${option.value}-${option.secondaryLabel || ''}`}>
      <Box><Typography variant="body2">{option.label}</Typography>{option.secondaryLabel && option.secondaryLabel !== option.label && <Typography variant="caption" color="text.secondary">{option.secondaryLabel}</Typography>}</Box>
    </Box>}
    renderInput={params => <TextField {...params} size="small" label={label} placeholder={placeholder} InputProps={{ ...params.InputProps, endAdornment: <>{loading && <CircularProgress color="inherit" size={16} />}{params.InputProps.endAdornment}</> }} />}
  />
}
